import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  resource,
  signal
} from '@angular/core';
import { debounceTime, distinctUntilChanged, firstValueFrom, tap } from 'rxjs';
import { BaseComponent } from '~console/app/~shared/abstract-components/base.component';
import { OrganizationService } from '~console/app/~shared/services/organization.service';
import { ToastService } from '~console/app/~shared/services/toast.service';
import { getSerializableHttpError } from '@veone/ngx-utilities/http';
import { FormControl, Validators } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Organization } from '~core/models/organization.models';
import { DialogService } from '~console/app/~shared/services/dialog.service';

@Component({
  selector: 'organization-switcher-dialog',
  standalone: false,
  templateUrl: './organization-switcher-dialog.component.html',
  styleUrl: './organization-switcher-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrganizationSwitcherDialogComponent
  extends BaseComponent
  implements AfterContentInit
{
  protected _organizationService = inject(OrganizationService);
  protected _dialogService = inject(DialogService);
  protected _toastService = inject(ToastService);

  currentOrganization$ = this._dashboardFacade.currentOrganization$;

  organizationResource = resource({
    request: () => {
      const name = this.name();
      return {
        ...(name && { name: `like(%${name}%)` }),
        perPage: 8,
        onboardingCompleted: true
      };
    },
    loader: ({ request }) =>
      firstValueFrom(this._organizationService.list(request))
  });

  organizationData = computed(() => {
    return this.organizationResource.value();
  });

  currentError = computed(() => {
    const error = this.organizationResource.error();
    if (error) {
      return getSerializableHttpError(error);
    }
    return null;
  });

  currentError$ = toObservable(this.currentError);

  isLoading = computed(() => {
    return this.organizationResource.isLoading();
  });

  name = signal<string | undefined>(undefined);

  searchControl = new FormControl<string | null>('', [
    Validators.required,
    Validators.minLength(2)
  ]);

  ngAfterContentInit(): void {
    this.listenToSearchControlChanges();
  }

  listenToSearchControlChanges(): void {
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        debounceTime(1000),
        distinctUntilChanged(),
        tap(name => {
          if (!name) {
            this.clearSearch();
            return;
          }
          if (!this.searchControl.valid) return;
          this.name.set(name);
        })
      )
      .subscribe();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.name.set(undefined);
  }

  selectOrganization(organization: Organization): void {
    this._dashboardFacade.setCurrentOrganization({ organization });
    this._dialogService.closeAll();
  }

  close(): void {
    this._dialogService.closeAll();
  }
}
